/**
 * Add / edit one recurring money item.
 *
 * The schedule picker changes shape with the kind, because asking for a
 * day-of-month on a weekly item is how you end up with data that projects to
 * nowhere. Only the fields that apply are shown, and only those are saved —
 * switching an item from monthly to weekly clears the stale dayOfMonth rather
 * than leaving it on the document.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { RecurrenceKind, RecurringMoneyItem, TxScope } from '../../types';
import { NewRecurringMoneyItem, useRecurringMoney } from '../../contexts/RecurringMoneyContext';
import { SEED_CATEGORIES } from '../../utils/categories';
import { DAY_NAMES, MONTH_NAMES, isDayString, monthlyEquivalent, ordinal } from '../../utils/rhythm';
import { guessCategory } from '../../utils/rhythmImport';

interface Props {
  show: boolean;
  onHide: () => void;
  /** null = create. */
  editing: RecurringMoneyItem | null;
}

const KIND_OPTIONS: Array<{ value: RecurrenceKind; label: string; hint: string }> = [
  { value: 'monthly_day', label: 'Monthly, on a set day', hint: 'Rent on the 1st, phone on the 14th' },
  { value: 'monthly_spread', label: 'Monthly, no set day', hint: 'Groceries and fuel — spread evenly across the month' },
  { value: 'weekly', label: 'Weekly', hint: 'Every Friday' },
  { value: 'fortnightly', label: 'Fortnightly', hint: 'Every 2 weeks from a date you pick' },
  { value: 'quarterly', label: 'Quarterly', hint: 'Every 3 months' },
  { value: 'yearly', label: 'Yearly', hint: 'Insurance, registration' },
  { value: 'one_off', label: 'One-off', hint: 'A single dated payment' },
];

const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface FormState {
  name: string;
  direction: 'in' | 'out';
  amount: string;
  expectedAmount: string;
  kind: RecurrenceKind;
  dayOfMonth: number;
  weekday: number;
  month: number;
  anchorDate: string;
  startDate: string;
  endDate: string;
  category: string;
  scope: TxScope;
  active: boolean;
  notes: string;
}

const emptyForm = (): FormState => ({
  name: '',
  direction: 'out',
  amount: '',
  expectedAmount: '',
  kind: 'monthly_day',
  dayOfMonth: 1,
  weekday: 5,
  month: 1,
  anchorDate: todayLocal(),
  startDate: '',
  endDate: '',
  category: 'expense:other',
  scope: 'personal',
  active: true,
  notes: '',
});

const fromItem = (it: RecurringMoneyItem): FormState => ({
  name: it.name,
  direction: it.direction,
  amount: String(it.amount),
  // `0` must render as "0", not as an empty box that reads like "unset".
  expectedAmount: it.expectedAmount === undefined ? '' : String(it.expectedAmount),
  kind: it.kind,
  dayOfMonth: it.dayOfMonth ?? 1,
  weekday: it.weekday ?? 5,
  month: it.month ?? 1,
  anchorDate: it.anchorDate || todayLocal(),
  startDate: it.startDate || '',
  endDate: it.endDate || '',
  category: it.category,
  scope: it.scope,
  active: it.active,
  notes: it.notes || '',
});

export function RecurringItemModal({ show, onHide, editing }: Props) {
  const { addItem, replaceItem, deleteItem } = useRecurringMoney();
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Once the user picks a category by hand, stop overwriting it as they type.
  const [categoryTouched, setCategoryTouched] = useState(false);

  useEffect(() => {
    if (!show) return;
    setForm(editing ? fromItem(editing) : emptyForm());
    setConfirmDelete(false);
    setCategoryTouched(!!editing);
  }, [show, editing]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Guess a category from the name while it's still untouched — most bills are
  // recognisable and it saves a trip to the dropdown.
  useEffect(() => {
    if (categoryTouched || !form.name.trim()) return;
    const guess = guessCategory(form.name, form.direction);
    setForm((f) => (f.category === guess ? f : { ...f, category: guess }));
  }, [form.name, form.direction, categoryTouched]);

  const amountNum = Number(form.amount);
  const expectedNum = form.expectedAmount.trim() === '' ? null : Number(form.expectedAmount);

  const errors = useMemo(() => {
    const e: string[] = [];
    if (!form.name.trim()) e.push('Give it a name.');
    if (!Number.isFinite(amountNum) || amountNum <= 0) e.push('Amount must be a number above zero.');
    if (expectedNum !== null && (!Number.isFinite(expectedNum) || expectedNum < 0)) {
      e.push('"Usually" must be a number of zero or more.');
    }
    if ((form.kind === 'one_off' || form.kind === 'fortnightly' || form.kind === 'quarterly') && !isDayString(form.anchorDate)) {
      e.push('Pick a valid date.');
    }
    if (form.startDate && !isDayString(form.startDate)) e.push('Start date is not a valid date.');
    if (form.endDate && !isDayString(form.endDate)) e.push('End date is not a valid date.');
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      e.push('End date is before the start date.');
    }
    return e;
  }, [form, amountNum, expectedNum]);

  /** Only the fields this kind actually uses. Everything else is left off the
   *  document entirely. */
  const buildPayload = (): NewRecurringMoneyItem => {
    const base: NewRecurringMoneyItem = {
      name: form.name.trim(),
      direction: form.direction,
      amount: amountNum,
      ...(expectedNum !== null && expectedNum !== amountNum ? { expectedAmount: expectedNum } : {}),
      kind: form.kind,
      category: form.category,
      scope: form.scope,
      active: form.active,
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      ...(form.startDate ? { startDate: form.startDate } : {}),
      ...(form.endDate ? { endDate: form.endDate } : {}),
    };
    switch (form.kind) {
      case 'monthly_day':
        return { ...base, dayOfMonth: form.dayOfMonth };
      case 'yearly':
        return { ...base, month: form.month, dayOfMonth: form.dayOfMonth };
      case 'weekly':
        return { ...base, weekday: form.weekday };
      case 'fortnightly':
      case 'quarterly':
      case 'one_off':
        return { ...base, anchorDate: form.anchorDate };
      default:
        return base;
    }
  };

  const preview = useMemo(() => {
    if (errors.length) return null;
    const payload = buildPayload();
    const full = monthlyEquivalent({ ...payload, id: 'preview' } as RecurringMoneyItem, 'full');
    const expected = monthlyEquivalent({ ...payload, id: 'preview' } as RecurringMoneyItem, 'expected');
    return { full, expected, differs: Math.abs(full - expected) > 0.005 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, errors.length]);

  const handleSave = async () => {
    if (errors.length) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editing) {
        await replaceItem(editing.id, payload);
        toast.success(`Updated ${payload.name}`);
      } else {
        await addItem(payload);
        toast.success(`Added ${payload.name}`);
      }
      onHide();
    } catch (err: any) {
      toast.error(err?.message || 'Could not save that item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await deleteItem(editing.id);
      toast.success(`Deleted ${editing.name}`);
      onHide();
    } catch (err: any) {
      toast.error(err?.message || 'Could not delete that item');
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = useMemo(
    () =>
      SEED_CATEGORIES.filter((c) =>
        form.direction === 'in' ? c.bucket === 'income' : c.bucket === 'expense'
      ),
    [form.direction]
  );

  return (
    <Modal show={show} onHide={saving ? () => {} : onHide} size="lg" backdrop={saving ? 'static' : true} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{editing ? 'Edit recurring item' : 'Add a recurring item'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-12 col-md-7">
            <label className="form-label fw-semibold mb-1">Name</label>
            <input
              className="form-control"
              value={form.name}
              autoFocus
              placeholder="Council Rates"
              onChange={(e) => set('name', e.target.value)}
            />
          </div>

          <div className="col-12 col-md-5">
            <label className="form-label fw-semibold mb-1">Direction</label>
            <div className="btn-group w-100" role="group">
              <button
                type="button"
                className={`btn btn-sm ${form.direction === 'out' ? 'btn-danger' : 'btn-light'}`}
                onClick={() => set('direction', 'out')}
              >
                <i className="bi bi-arrow-down-left me-1" /> Money out
              </button>
              <button
                type="button"
                className={`btn btn-sm ${form.direction === 'in' ? 'btn-success' : 'btn-light'}`}
                onClick={() => {
                  set('direction', 'in');
                  if (!categoryTouched) set('category', 'income:other');
                }}
              >
                <i className="bi bi-arrow-up-right me-1" /> Money in
              </button>
            </div>
          </div>

          <div className="col-6 col-md-4">
            <label className="form-label fw-semibold mb-1">Amount</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
              />
            </div>
          </div>

          <div className="col-6 col-md-4">
            <label className="form-label fw-semibold mb-1">
              Usually
              <i
                className="bi bi-info-circle text-muted ms-1"
                title="The realistic figure when it differs from the bill — electricity billed at $500 that normally lands nearer $300. Enter 0 for something you don't expect to pay this cycle. Leave blank if it's always the full amount."
              />
            </label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                placeholder="same"
                value={form.expectedAmount}
                onChange={(e) => set('expectedAmount', e.target.value)}
              />
            </div>
          </div>

          <div className="col-12 col-md-4">
            <label className="form-label fw-semibold mb-1">Books</label>
            <select
              className="form-select"
              value={form.scope}
              onChange={(e) => set('scope', e.target.value as TxScope)}
            >
              <option value="personal">Family</option>
              <option value="business">Business</option>
            </select>
          </div>

          {/* --- schedule ---------------------------------------------- */}
          <div className="col-12">
            <hr className="my-1" />
            <label className="form-label fw-semibold mb-1">Repeats</label>
            <select
              className="form-select"
              value={form.kind}
              onChange={(e) => set('kind', e.target.value as RecurrenceKind)}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            <div className="form-text">{KIND_OPTIONS.find((k) => k.value === form.kind)?.hint}</div>
          </div>

          {form.kind === 'monthly_day' && (
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold mb-1">Day of month</label>
              <select
                className="form-select"
                value={form.dayOfMonth}
                onChange={(e) => set('dayOfMonth', Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {ordinal(d)}
                  </option>
                ))}
              </select>
              {form.dayOfMonth > 28 && (
                <div className="form-text">
                  Short months use their last day — the {ordinal(form.dayOfMonth)} becomes the 28th in February
                  and returns to the {ordinal(form.dayOfMonth)} after.
                </div>
              )}
            </div>
          )}

          {form.kind === 'weekly' && (
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold mb-1">Day</label>
              <select
                className="form-select"
                value={form.weekday}
                onChange={(e) => set('weekday', Number(e.target.value))}
              >
                {DAY_NAMES.map((d, i) => (
                  <option key={d} value={i}>
                    {d}
                  </option>
                ))}
              </select>
              <div className="form-text">Some months have five — the forecast counts them all.</div>
            </div>
          )}

          {form.kind === 'yearly' && (
            <>
              <div className="col-6 col-md-4">
                <label className="form-label fw-semibold mb-1">Month</label>
                <select className="form-select" value={form.month} onChange={(e) => set('month', Number(e.target.value))}>
                  {MONTH_NAMES.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label fw-semibold mb-1">Day</label>
                <select
                  className="form-select"
                  value={form.dayOfMonth}
                  onChange={(e) => set('dayOfMonth', Number(e.target.value))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {ordinal(d)}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {(form.kind === 'fortnightly' || form.kind === 'quarterly' || form.kind === 'one_off') && (
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold mb-1">
                {form.kind === 'one_off' ? 'Date' : 'Starting from'}
              </label>
              <input
                type="date"
                className="form-control"
                value={form.anchorDate}
                onChange={(e) => set('anchorDate', e.target.value)}
              />
              {form.kind !== 'one_off' && (
                <div className="form-text">Any occurrence will do — the series is counted from it both ways.</div>
              )}
            </div>
          )}

          <div className="col-12">
            <label className="form-label fw-semibold mb-1">Category</label>
            <select
              className="form-select"
              value={form.category}
              onChange={(e) => {
                setCategoryTouched(true);
                set('category', e.target.value);
              }}
            >
              {categoryOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
              {/* A category from an import that isn't in the seed list must
                  still be selectable, or saving would silently change it. */}
              {!categoryOptions.some((c) => c.id === form.category) && (
                <option value={form.category}>{form.category}</option>
              )}
            </select>
          </div>

          {/* --- optional bounds ---------------------------------------- */}
          <div className="col-6 col-md-4">
            <label className="form-label fw-semibold mb-1">
              Starts <span className="text-muted fw-normal">(optional)</span>
            </label>
            <input type="date" className="form-control" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </div>
          <div className="col-6 col-md-4">
            <label className="form-label fw-semibold mb-1">
              Ends <span className="text-muted fw-normal">(optional)</span>
            </label>
            <input type="date" className="form-control" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            <div className="form-text">For a fixed-term contract or a loan that finishes.</div>
          </div>
          <div className="col-12 col-md-4 d-flex align-items-end">
            <div className="form-check form-switch mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="rm-active"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="rm-active">
                Include in the forecast
              </label>
            </div>
          </div>

          <div className="col-12">
            <label className="form-label fw-semibold mb-1">
              Notes <span className="text-muted fw-normal">(optional)</span>
            </label>
            <input className="form-control" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </div>

        {preview && (
          <div className="alert alert-light border mt-3 mb-0 py-2 px-3 d-flex align-items-center gap-2">
            <i className="bi bi-calculator text-muted" />
            <span>
              Works out to{' '}
              <strong>
                {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(preview.full)}
              </strong>{' '}
              a month
              {preview.differs && (
                <>
                  {' '}
                  ·{' '}
                  <strong>
                    {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(preview.expected)}
                  </strong>{' '}
                  on the "usually" figure
                </>
              )}
              {form.kind === 'one_off' && <span className="text-muted"> — one-offs have no monthly rate</span>}
            </span>
          </div>
        )}

        {errors.length > 0 && (
          <div className="alert alert-warning mt-3 mb-0 py-2 px-3">
            <ul className="mb-0 ps-3">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {editing &&
            (confirmDelete ? (
              <>
                <span className="text-danger me-2 small">Delete "{editing.name}"?</span>
                <button className="btn btn-sm btn-danger me-1" onClick={handleDelete} disabled={saving}>
                  Yes, delete
                </button>
                <button className="btn btn-sm btn-light" onClick={() => setConfirmDelete(false)} disabled={saving}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="btn btn-sm btn-light-danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
                <i className="bi bi-trash me-1" /> Delete
              </button>
            ))}
        </div>
        <div>
          <button className="btn btn-light me-2" onClick={onHide} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || errors.length > 0}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add item'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
